/**
 * OpsTrainingCompletions — Operations Dashboard read-only view of
 * training completions. All training module management (create,
 * assign, edit, expire) lives in Safety / Marketing / Sales / Legal
 * command centers via the Quick Access strip. Ops only sees WHO has
 * finished WHAT so field supervisors can dispatch appropriately.
 *
 * Data comes from GET /api/training/completions which returns a flat
 * list of { user_id, module_id, completed_at, score, ... } records.
 */
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { GraduationCap, Search, Award, Calendar } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OpsTrainingCompletions() {
  const [completions, setCompletions] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setFetchError(null);
      const errs = [];
      try {
        const [cRes, mRes, uRes] = await Promise.all([
          fetch(`${API}/api/training/completions`, { credentials: 'include' }),
          fetch(`${API}/api/training/modules`, { credentials: 'include' }),
          fetch(`${API}/api/company/employees`, { credentials: 'include' }),
        ]);
        if (!alive) return;
        if (cRes.ok) {
          const cData = await cRes.json();
          setCompletions(cData.completions || cData || []);
        } else errs.push('completions');
        if (mRes.ok) {
          const mData = await mRes.json();
          setModules(mData.modules || mData || []);
        } else errs.push('modules');
        if (uRes.ok) {
          const uData = await uRes.json();
          setUsers(Array.isArray(uData) ? uData : (uData.employees || []));
        } else errs.push('roster');
        if (errs.length) setFetchError(errs.join(', '));
      } catch (e) {
        if (alive) setFetchError('network');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Index modules + users for O(1) lookup in the table row renderer.
  const moduleById = useMemo(() => {
    const map = {};
    (modules || []).forEach((m) => { map[m.module_id || m.id] = m; });
    return map;
  }, [modules]);
  const userById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => { map[u.user_id] = u; });
    return map;
  }, [users]);

  const filtered = useMemo(() => {
    if (!q) return completions;
    const needle = q.toLowerCase();
    return completions.filter((c) => {
      const mod = moduleById[c.module_id] || {};
      const usr = userById[c.user_id] || {};
      const hay = `${usr.name || ''} ${usr.badge_number || ''} ${mod.title || c.module_id || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [completions, q, moduleById, userById]);

  return (
    <div className="space-y-4" data-testid="ops-training-completions">
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <GraduationCap className="h-6 w-6 text-[#C9A962]" />
              TRAINING COMPLETIONS · READ-ONLY
            </h2>
            <p className="text-xs text-zinc-400 mt-1">
              Operations view · Module authoring &amp; assignments live in
              <span className="text-[#C9A962] font-semibold"> Safety / Marketing / Sales / Legal Command Centers</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-[#C9A962] text-black font-bold">
              <Award className="h-3 w-3 mr-1" /> {filtered.length} certifications
            </Badge>
            <Badge className="bg-zinc-700 text-white">{modules.length} modules</Badge>
          </div>
        </div>

        <div className="relative mt-4 max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search agent, badge, module…"
            className="pl-8 bg-zinc-950 border-zinc-700 text-white"
            data-testid="ops-training-search"
          />
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-700 overflow-hidden">
        {fetchError && (
          <div className="bg-amber-900/30 border-b border-amber-700 px-4 py-2 text-xs text-amber-200 flex items-center gap-2"
               data-testid="ops-training-fetch-error">
            <span className="font-semibold uppercase tracking-widest">Partial data</span>
            <span>Failed to load: {fetchError} — some rows may show raw IDs.</span>
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading completions…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ops-training-table">
              <thead className="bg-zinc-950 text-zinc-400 uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="px-4 py-2 text-left">Agent</th>
                  <th className="px-4 py-2 text-left">Module</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Score</th>
                  <th className="px-4 py-2 text-left">Completed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-10 text-center text-zinc-500"
                        data-testid="ops-training-empty">
                      No completions on file yet.
                    </td>
                  </tr>
                )}
                {filtered.map((c, idx) => {
                  const mod = moduleById[c.module_id] || {};
                  const usr = userById[c.user_id] || {};
                  return (
                    <tr key={c.completion_id || `${c.user_id}-${c.module_id}-${idx}`}
                        className="border-t border-zinc-800 hover:bg-zinc-950/60"
                        data-testid={`ops-training-row-${c.completion_id || idx}`}>
                      <td className="px-4 py-2 text-white font-semibold">
                        <div>{usr.name || c.user_id || '—'}</div>
                        {usr.badge_number && (
                          <div className="text-[11px] text-zinc-500">{usr.badge_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-zinc-200">{mod.title || c.module_id || '—'}</td>
                      <td className="px-4 py-2 text-zinc-400 text-xs">
                        <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                          {mod.category || 'general'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-zinc-200">
                        {c.score != null ? `${c.score}%` : '—'}
                      </td>
                      <td className="px-4 py-2 text-zinc-300 text-xs flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-zinc-500" />
                        {c.completed_at
                          ? new Date(c.completed_at).toLocaleDateString()
                          : (c.created_at ? new Date(c.created_at).toLocaleDateString() : '—')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
